import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { TierBadge, Pill, Avatar, WeatherBadge, timeAgo, daysUntil } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Sparkles, Copy, Send, FileUp, ArrowUpRight, Wand2, Lock, ShieldAlert, Mail, Compass,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AlmirasDesk({ initialMode = "reply" }) {
  const [inbox, setInbox] = useState([]);
  const [selected, setSelected] = useState(null);
  const [paste, setPaste] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refining, setRefining] = useState(false);
  const [mode, setMode] = useState(initialMode);

  useEffect(() => { setMode(initialMode); }, [initialMode]);

  useEffect(() => { api.get("/inbox").then(({ data }) => setInbox(data)); }, []);

  const draftFrom = async (payload, email) => {
    setLoading(true); setResult(null); setSelected(email || null);
    try {
      const { data } = await api.post("/almira/draft", payload);
      setResult(data);
    } catch { toast.error("Couldn't generate a draft."); }
    finally { setLoading(false); }
  };

  const refine = async (instruction) => {
    if (!result) return;
    setRefining(true);
    try {
      const { data } = await api.post("/almira/refine", { draft: result.draft, instruction });
      setResult({ ...result, draft: data.draft });
      toast.success("Draft updated");
    } finally { setRefining(false); }
  };

  const action = async (a) => {
    const { data } = await api.post("/almira/action", { action: a });
    toast.success(data.message);
  };

  const escalate = async () => {
    await api.post("/almira/escalate", {
      contact_id: result.contact?.id, draft: result.draft,
      subject: result.email?.subject || "Client reply", tier: result.tier,
    });
    toast.success("Escalated to the Review Pipeline");
  };

  return (
    <div className="p-8 md:p-10">
      <PageHeader title={mode === "guidance" ? "Client Guidance Desk" : "Client Email Reply Assistant"}
        subtitle={mode === "guidance"
          ? "Grounded, plain-English guidance on the tricky judgment calls — sourced from our SOPs and the Legally Legit book. We never auto-send."
          : "Draft on-brand, plain-English client replies grounded in our SOPs and the Legally Legit book. We never auto-send."} />

      {mode === "guidance" ? <GuidanceDesk /> : (
      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        {/* Left: inbox + paste */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-black/5">
            <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-2">Paste a client email</div>
            <Textarea data-testid="paste-email" value={paste} onChange={(e) => setPaste(e.target.value)} rows={4}
              placeholder="Paste the email body here…" className="bg-[#F2E9DB] border-[#BDC5DB] text-sm" />
            <Button data-testid="draft-from-paste" disabled={!paste.trim() || loading}
              onClick={() => draftFrom({ body: paste, subject: "Pasted email" })}
              className="w-full mt-3 bg-[#FFD100] text-[#101820] font-bold hover:brightness-95">
              <Sparkles className="w-4 h-4 mr-1.5" /> Draft a reply
            </Button>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-2 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> {inbox[0]?.live ? "Live inbox · hello@" : "Client inbox"}
            </div>
            <div className="space-y-2">
              {inbox.length === 0 && <div className="text-sm text-[#101820]/40 bg-white rounded-xl p-3.5 shadow-sm">No client threads waiting. Paste an email above to draft a reply.</div>}
              {inbox.map((e) => (
                <button key={e.id} data-testid={`inbox-email-${e.id}`} onClick={() => draftFrom({ email_id: e.id }, e)}
                  className={`w-full text-left bg-white rounded-xl p-3.5 shadow-sm border transition-all card-lift ${
                    selected?.id === e.id ? "border-[#1D57A5] ring-1 ring-[#1D57A5]" : "border-black/5"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-[#141B4D] text-sm truncate">{e.contact?.name || "Client"}</span>
                    <span className="text-[11px] text-[#101820]/40 shrink-0">{timeAgo(e.received)}</span>
                  </div>
                  <div className="text-sm text-[#101820]/80 mt-0.5 truncate">{e.subject}</div>
                  <div className="text-xs text-[#101820]/45 mt-0.5 line-clamp-1">{e.body}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right: snapshot + draft */}
        <div className="space-y-5">
          {!result && !loading && (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-black/5">
              <Sparkles className="w-10 h-10 text-[#BDC5DB] mx-auto mb-3" />
              <div className="text-[#101820]/60">Pick an email or paste one to get started.</div>
            </div>
          )}
          {loading && (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-black/5">
              <div className="text-[#141B4D] font-semibold animate-pulse">Reading the email, matching the client, drafting in our voice…</div>
            </div>
          )}

          {result && (
            <>
              <ClientSnapshot contact={result.contact} matter={result.matter} />

              <div className="bg-white rounded-xl shadow-sm border border-black/5 overflow-hidden">
                <div className="section-band px-5 py-4 flex items-center justify-between flex-wrap gap-2">
                  <TierBadge tier={result.tier} />
                  <div className="flex items-center gap-3 text-white text-sm">
                    <span>Confidence <span className="font-bold text-[#FFD100]">{result.confidence}%</span></span>
                    <Pill tone={result.ai ? "green" : "pale"}>{result.ai ? "Claude Sonnet 4.6" : "Rules fallback"}</Pill>
                  </div>
                </div>

                <div className="p-5">
                  {result.tier === 3 && (
                    <div className="mb-4 rounded-lg bg-[#FBE0E0] border border-[#B91C1C]/20 p-3 flex items-start gap-2">
                      <ShieldAlert className="w-5 h-5 text-[#B91C1C] shrink-0 mt-0.5" />
                      <div className="text-sm text-[#B91C1C]">
                        <span className="font-bold">Legal judgment needed.</span> This is a warm holding reply. It's locked until an attorney approves it in the Review Pipeline.
                      </div>
                    </div>
                  )}

                  <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-2">Draft reply · inline editable</div>
                  <Textarea data-testid="draft-text" value={result.draft} onChange={(e) => setResult({ ...result, draft: e.target.value })}
                    rows={9} className="bg-[#F2E9DB] border-[#BDC5DB] text-[15px] leading-relaxed" />

                  {result.reasoning && <div className="text-xs text-[#101820]/45 mt-2 italic">Why this tier: {result.reasoning}</div>}

                  {result.sources?.length > 0 && (
                    <div className="mt-3">
                      <span className="text-xs font-bold uppercase tracking-widest text-[#101820]/50">Sources: </span>
                      {result.sources.map((s, i) => <Pill key={i} tone="cobalt" className="ml-1 mb-1">{s}</Pill>)}
                    </div>
                  )}

                  {/* Tone controls */}
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 flex items-center gap-1"><Wand2 className="w-3.5 h-3.5" /> Tone:</span>
                    {[["warmer", "Warmer"], ["shorter", "Shorter"], ["plain", "More plain-English"]].map(([k, l]) => (
                      <Button key={k} data-testid={`tone-${k}`} disabled={refining} variant="outline" onClick={() => refine(k)}
                        className="h-8 text-xs border-[#1D57A5] text-[#1D57A5] hover:bg-[#1D57A5] hover:text-white">{l}</Button>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="mt-5 pt-4 border-t border-[#BDC5DB]/40 flex items-center gap-2 flex-wrap">
                    <Button data-testid="action-copy" variant="outline" onClick={() => { navigator.clipboard?.writeText(result.draft); action("copy"); }}
                      className="h-9 border-[#1D57A5] text-[#1D57A5] hover:bg-[#1D57A5] hover:text-white"><Copy className="w-4 h-4 mr-1.5" /> Copy</Button>
                    {result.can_send ? (
                      <Button data-testid="action-send" onClick={() => action("send")} className="h-9 bg-[#FFD100] text-[#101820] font-bold hover:brightness-95"><Send className="w-4 h-4 mr-1.5" /> Send (mock)</Button>
                    ) : (
                      <Button data-testid="action-send-locked" disabled className="h-9 bg-[#BDC5DB] text-[#141B4D]"><Lock className="w-4 h-4 mr-1.5" /> Send locked (needs approval)</Button>
                    )}
                    <Button data-testid="action-log" variant="outline" onClick={() => action("log_hubspot")}
                      className="h-9 border-[#1D57A5] text-[#1D57A5] hover:bg-[#1D57A5] hover:text-white"><FileUp className="w-4 h-4 mr-1.5" /> Log to HubSpot (mock)</Button>
                    <Button data-testid="action-escalate" onClick={escalate} className="h-9 bg-[#1D57A5] text-white hover:bg-[#141B4D]"><ArrowUpRight className="w-4 h-4 mr-1.5" /> Escalate to Review</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function ClientSnapshot({ contact, matter }) {
  if (!contact) return (
    <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-[#B45309]">
      <div className="text-sm text-[#B45309] font-semibold">No matching HubSpot contact found for this sender.</div>
      <div className="text-xs text-[#101820]/50 mt-1">Route to an owner to confirm identity before replying.</div>
    </div>
  );
  const oa = daysUntil(matter?.office_action_due), sou = daysUntil(matter?.sou_due);
  return (
    <div data-testid="client-snapshot" className="bg-white rounded-xl p-5 shadow-md border-l-4 border-[#1D57A5]">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Avatar name={contact.name} size={44} />
          <div>
            <div className="font-bold text-[#141B4D]">{contact.name}</div>
            <div className="text-xs text-[#101820]/55">{contact.company} · {contact.email}</div>
          </div>
        </div>
        {matter && <WeatherBadge value={matter.weather} />}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
        <Field label="Matter stage" value={matter?.stage} />
        <Field label="Serial" value={matter?.serial_number} mono />
        <Field label="Registration" value={matter?.registration_number} mono />
        <Field label="Owner" value={matter?.owner?.split("@")[0]} />
        <Field label="Mark" value={contact.mark} />
        <Field label="Goods/services" value={contact.goods_services} />
        <Field label="Use basis" value={contact.use_basis} />
        <Field label="Intake confidence" value={contact.confidence} />
        {oa !== null && <Field label="OA due" value={`${matter.office_action_due} (${oa}d)`} danger={oa < 14} />}
        {sou !== null && <Field label="SOU due" value={`${matter.sou_due} (${sou}d)`} danger={sou < 14} />}
        {matter?.s8_earliest && <Field label="§8 earliest" value={matter.s8_earliest} />}
        {matter?.s8_latest && <Field label="§8 latest" value={matter.s8_latest} />}
      </div>
      <div className="text-[11px] text-[#101820]/40 mt-3">Read-only snapshot from HubSpot.</div>
    </div>
  );
}

function Field({ label, value, mono, danger }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#101820]/40">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${danger ? "text-[#B91C1C] font-semibold" : "text-[#141B4D]"} mt-0.5`}>{value}</div>
    </div>
  );
}


function GuidanceDesk() {
  const [scenarios, setScenarios] = useState({});
  const [scenario, setScenario] = useState("class_description");
  const [situation, setSituation] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState("none");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/almira/guidance-scenarios").then(({ data }) => setScenarios(data));
    api.get("/spotlight/search", { params: { q: "" } }).then(() => {}).catch(() => {});
    api.get("/inbox").then(({ data }) => {
      const cs = data.map((e) => e.contact).filter(Boolean);
      const uniq = Object.values(Object.fromEntries(cs.map((c) => [c.id, c])));
      setContacts(uniq);
    });
  }, []);

  const EXAMPLES = {
    class_description: "Client sells handmade candles AND runs candle-making workshops. They want everything in one class to save money. How should we structure the classes & descriptions?",
    search_conclusion: "Search on 'NIMBUS' for fitness apps found 2 live marks in Class 009 for unrelated software and 1 dead mark in Class 041. What should our conclusion and Weather badge be?",
    refund_goodwill: "Client is upset about a delay on their office action and is asking for a partial refund. What's our move?",
    llc_formation: "A trademark client just asked us to also form their LLC in Texas. How do we handle that request?",
    ein_issue: "Client's SOU is ready but their business entity name changed and the EIN is under the old name. How do we proceed?",
    other: "",
  };

  const ask = async () => {
    if (!situation.trim()) return;
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/almira/guidance", {
        scenario_type: scenario, situation, contact_id: contactId === "none" ? null : contactId,
      });
      setRes(data);
    } finally { setLoading(false); }
  };

  const escalate = async () => {
    await api.post("/almira/escalate", {
      contact_id: contactId === "none" ? null : contactId,
      draft: `GUIDANCE QUESTION (${scenarios[scenario] || scenario}):\n${situation}\n\nAI RECOMMENDATION:\n${res?.recommendation}`,
      subject: "Guidance escalation", tier: 3,
    });
    toast.success("Escalated to the Review Pipeline");
  };

  return (
    <div className="grid lg:grid-cols-[420px_1fr] gap-6 brand-fade">
      <div className="bg-white rounded-xl p-5 shadow-sm border border-black/5 space-y-4 h-fit">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-1.5">What kind of call is this?</div>
          <Select value={scenario} onValueChange={(v) => { setScenario(v); if (!situation) setSituation(EXAMPLES[v] || ""); }}>
            <SelectTrigger data-testid="guidance-scenario" className="bg-[#F2E9DB] border-[#BDC5DB]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(scenarios).map(([k, v]) => <SelectItem key={k} value={k}>{v.split("—")[0].trim()}</SelectItem>)}
            </SelectContent>
          </Select>
          {scenarios[scenario] && <div className="text-xs text-[#101820]/45 mt-1.5">{scenarios[scenario]}</div>}
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-1.5">Related client (optional)</div>
          <Select value={contactId} onValueChange={setContactId}>
            <SelectTrigger data-testid="guidance-contact" className="bg-[#F2E9DB] border-[#BDC5DB]"><SelectValue placeholder="No specific client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No specific client</SelectItem>
              {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.mark}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-1.5">Describe the situation</div>
          <Textarea data-testid="guidance-situation" value={situation} onChange={(e) => setSituation(e.target.value)} rows={6}
            placeholder={EXAMPLES[scenario]} className="bg-[#F2E9DB] border-[#BDC5DB] text-sm" />
        </div>

        <Button data-testid="guidance-ask" disabled={!situation.trim() || loading} onClick={ask}
          className="w-full bg-[#FFD100] text-[#101820] font-bold hover:brightness-95">
          <Compass className="w-4 h-4 mr-1.5" /> Get guidance
        </Button>
      </div>

      <div className="space-y-4">
        {!res && !loading && (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-black/5">
            <Compass className="w-10 h-10 text-[#BDC5DB] mx-auto mb-3" />
            <div className="text-[#101820]/60">Pick a scenario and describe the situation. I'll give you a grounded recommendation — not legal advice, and I'll flag anything that needs a reviewer or attorney.</div>
          </div>
        )}
        {loading && <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-black/5 text-[#141B4D] font-semibold animate-pulse">Thinking it through against our SOPs and the matter record…</div>}

        {res && (
          <div className="bg-white rounded-xl shadow-sm border border-black/5 overflow-hidden">
            <div className="section-band px-5 py-4 flex items-center justify-between flex-wrap gap-2">
              <span className="font-bold flex items-center gap-2"><Compass className="w-5 h-5 text-[#FFD100]" /> Recommended approach</span>
              <div className="flex items-center gap-2">
                <span className="text-white text-sm">Confidence <span className="font-bold text-[#FFD100]">{res.confidence}%</span></span>
                <Pill tone={res.ai ? "green" : "pale"}>{res.ai ? "Claude Sonnet 4.6" : "Fallback"}</Pill>
              </div>
            </div>
            <div className="p-5">
              {res.escalate && (
                <div className="mb-4 rounded-lg bg-[#FBE0E0] border border-[#B91C1C]/20 p-3 flex items-start gap-2">
                  <ShieldAlert className="w-5 h-5 text-[#B91C1C] shrink-0 mt-0.5" />
                  <div className="text-sm text-[#B91C1C]"><span className="font-bold">Escalate this.</span> {res.escalate_reason || "This needs a reviewer or attorney."}</div>
                </div>
              )}
              <p className="text-[15px] text-[#101820] leading-relaxed" data-testid="guidance-recommendation">{res.recommendation}</p>

              {res.considerations?.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-1.5">Things to weigh</div>
                  <ul className="list-disc list-inside text-sm text-[#101820]/80 space-y-1">
                    {res.considerations.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
              )}

              {res.client_note && (
                <div className="mt-4 callout rounded-lg p-4">
                  <div className="text-xs font-bold uppercase tracking-widest text-[#141B4D] mb-1.5">Optional note you could adapt for the client</div>
                  <div className="text-sm text-[#101820]">{res.client_note}</div>
                </div>
              )}

              {res.sources?.length > 0 && (
                <div className="mt-3">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#101820]/50">Sources: </span>
                  {res.sources.map((s, i) => <Pill key={i} tone="cobalt" className="ml-1 mb-1">{s}</Pill>)}
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-[#BDC5DB]/40 flex items-center gap-2 flex-wrap">
                <Button data-testid="guidance-copy" variant="outline" onClick={() => { navigator.clipboard?.writeText(res.recommendation); toast.success("Copied"); }}
                  className="h-9 border-[#1D57A5] text-[#1D57A5] hover:bg-[#1D57A5] hover:text-white"><Copy className="w-4 h-4 mr-1.5" /> Copy</Button>
                <Button data-testid="guidance-escalate" onClick={escalate} className="h-9 bg-[#1D57A5] text-white hover:bg-[#141B4D]"><ArrowUpRight className="w-4 h-4 mr-1.5" /> Escalate to Review</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
